import { useState, useEffect } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import type { Session } from '@/types'
import { supabase } from './lib/supabase'
import { useBiblionautasBadges } from './hooks/useBiblionautasBadges'
import { APP_VIEWS, AUTH_EVENTS } from './constants'
import * as Sentry from '@sentry/react'
import { LandingPage } from './components/LandingPage'
import BookScanner from './components/BookScanner'
import BookList from './components/BookList/index'
import Settings from './components/Settings/index'
import { Biblionautas } from './components/Biblionautas'
import ListsPage from './components/ListsPage'
import ChangePassword from './components/ChangePassword'
import { VersionDisplay } from './components/common/VersionDisplay'
import { ReleaseNotesModal } from './components/common/ReleaseNotesModal'
import { LATEST_RELEASE_NOTES } from './constants/releaseNotes'
import './App.css'

type AppView = typeof APP_VIEWS[keyof typeof APP_VIEWS]

function App() {
    const [session, setSession] = useState<Session | null>(null)
    const [view, setView] = useState<AppView>(APP_VIEWS.LIST)
    const [sharedUserId, setSharedUserId] = useState<string | null>(null)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [showReleaseNotes, setShowReleaseNotes] = useState(false)
    const { t } = useTranslation()

    const { totalBadge } = useBiblionautasBadges(session?.user?.id, {
        polling: true,
        pollingInterval: 30000,
        autoFetch: true,
    })

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setSession(session)
            if (session?.user) {
                Sentry.setUser({
                    id: session.user.id,
                    email: session.user.email
                })
            } else {
                Sentry.setUser(null)
            }
        })

        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange((event, session) => {
            setSession(session)
            if (session?.user) {
                Sentry.setUser({
                    id: session.user.id,
                    email: session.user.email
                })
            } else {
                Sentry.setUser(null)
            }

            if (event === AUTH_EVENTS.PASSWORD_RECOVERY) {
                setView(APP_VIEWS.CHANGE_PASSWORD)
            }
        })


        return () => subscription.unsubscribe()
    }, [])

    useEffect(() => {
        // Check if version has changed to show release notes
        // Suppress in test mode to avoid blocking E2E tests
        const isTestMode = import.meta.env.MODE === 'test'
        const lastSeenVersion = localStorage.getItem('last_seen_version')
        const currentVersion = __APP_VERSION__

        if (!isTestMode && session && lastSeenVersion !== currentVersion) {
            setTimeout(() => {
                setShowReleaseNotes(true)
            }, 0)
            localStorage.setItem('last_seen_version', currentVersion)
        }
    }, [session])

    const handleViewChange = (newView: AppView) => {
        if (newView === view && newView !== APP_VIEWS.SHARED_LIST) return
        setIsTransitioning(true)

        if (newView !== APP_VIEWS.SHARED_LIST) {
            setSharedUserId(null)
        }

        setTimeout(() => {
            setView(newView)
            setIsTransitioning(false)
        }, 150)
    }

    const handleViewLibrary = (userId: string) => {
        setSharedUserId(userId)
        handleViewChange(APP_VIEWS.SHARED_LIST)
    }

    const handleSignOut = async () => {
        await supabase.auth.signOut()
    }

    if (!session) {
        return <LandingPage isAuthenticated={false} onNavigateToLibrary={() => { }} />
    }

    if (view === APP_VIEWS.LANDING) {
        return <LandingPage isAuthenticated={true} onNavigateToLibrary={() => handleViewChange(APP_VIEWS.LIST)} />
    }

    const userEmail = session.user?.email || ''
    const userInitial = userEmail.charAt(0).toUpperCase()

    return (
        <div className="app-container">
            <nav className="navbar">
                <div className="navbar-content">
                    <button className="navbar-brand" onClick={() => handleViewChange(APP_VIEWS.LANDING)} aria-label={t('nav.home')}>
                        <span className="brand-icon">📚</span>
                        <span className="brand-text">{t('title')}</span>
                    </button>

                    <div className="navbar-tabs">
                        <button onClick={() => handleViewChange(APP_VIEWS.LIST)} className={`nav-tab ${view === APP_VIEWS.LIST ? 'active' : ''}`} data-testid="nav-library">
                            <span className="tab-icon">📖</span>
                            <span className="tab-text">{t('nav.library')}</span>
                        </button>
                        <button onClick={() => handleViewChange(APP_VIEWS.SCAN)} className={`nav-tab ${view === APP_VIEWS.SCAN ? 'active' : ''}`}>
                            <span className="tab-icon">📷</span>
                            <span className="tab-text">{t('nav.scan')}</span>
                        </button>
                        <button onClick={() => handleViewChange(APP_VIEWS.LISTS)} className={`nav-tab ${view === APP_VIEWS.LISTS ? 'active' : ''}`} data-testid="nav-lists">
                            <span className="tab-icon">📑</span>
                            <span className="tab-text">{t('nav.lists')}</span>
                        </button>
                        <button onClick={() => handleViewChange(APP_VIEWS.BIBLIONAUTAS)} className={`nav-tab ${view === APP_VIEWS.BIBLIONAUTAS ? 'active' : ''}`} data-testid="nav-biblionautas">
                            <span className="tab-icon">👩‍🚀</span>
                            <span className="tab-text">{t('nav.biblionautas')}</span>
                            {totalBadge > 0 && view !== APP_VIEWS.BIBLIONAUTAS && <span className="notification-badge-global">{totalBadge}</span>}
                        </button>
                        <button onClick={() => handleViewChange(APP_VIEWS.BIBLIOVERSO)} className={`nav-tab ${view === APP_VIEWS.BIBLIOVERSO ? 'active' : ''}`}>
                            <span className="tab-icon">🌌</span>
                            <span className="tab-text">{t('nav.biblioverso')}</span>
                        </button>
                    </div>

                    <div className="navbar-user">
                        <button className="user-avatar-btn" onClick={() => handleViewChange(APP_VIEWS.SETTINGS)} title={t('nav.settings')}>
                            <div className="user-avatar">
                                {session.user?.user_metadata?.avatar_url ? <img src={session.user.user_metadata.avatar_url} alt="Profile" /> : userInitial}
                            </div>
                        </button>
                        <button onClick={handleSignOut} className="signout-btn" aria-label={t('nav.logout')}>
                            <span className="signout-icon">🚪</span>
                            <span className="signout-text">{t('nav.logout')}</span>
                        </button>
                    </div>
                </div>
            </nav>

            <main className={`main-content ${isTransitioning ? 'transitioning' : ''}`}>
                {view === APP_VIEWS.LIST && <BookList session={session} />}
                {view === APP_VIEWS.SHARED_LIST && <BookList sharedUserId={sharedUserId} onBack={() => handleViewChange(APP_VIEWS.BIBLIONAUTAS)} session={session} />}
                {view === APP_VIEWS.SCAN && <BookScanner />}
                {view === APP_VIEWS.LISTS && <ListsPage />}
                {view === APP_VIEWS.BIBLIONAUTAS && (
                    <Biblionautas session={session} onBack={() => handleViewChange(APP_VIEWS.LIST)} onViewLibrary={handleViewLibrary} />
                )}
                {view === APP_VIEWS.BIBLIOVERSO && <BookList isBiblioverso={true} session={session} />}
                {view === APP_VIEWS.SETTINGS && <Settings onBack={() => handleViewChange(APP_VIEWS.LIST)} />}
                {view === APP_VIEWS.CHANGE_PASSWORD && <ChangePassword onComplete={() => handleViewChange(APP_VIEWS.LIST)} />}
            </main>

            <footer className="footer-credits">
                <div className="footer-content">
                    <p>
                        <Trans i18nKey="footer.made_with">
                            Hecho con <span className="heart">❤</span> por <a href="https://x.com/alpepas" target="_blank" rel="noopener noreferrer">@alpepas</a></Trans>
                    </p>
                    <span className="footer-divider">•</span>
                    <VersionDisplay onClick={() => setShowReleaseNotes(true)} />
                </div>
            </footer>

            <ReleaseNotesModal 
                isOpen={showReleaseNotes} 
                onClose={() => setShowReleaseNotes(false)} 
                version={__APP_VERSION__}
                notes={LATEST_RELEASE_NOTES}
            />
        </div>
    )
}

export default App
